import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, UserWorkspace } from '@altsui/shared';
import { authApi } from '@/lib/api/auth';
import { queryClient } from '@/lib/queryClient';
import { clearStoredAuthSession } from '@/lib/auth/session';
import { publishActiveFundContext } from '@/lib/auth/activeFundContext';
import { startCrossTabAuthSync } from '@/lib/auth/crossTabAuthSync';
import { ApiRequestError } from '@/lib/api/client';

interface AuthState {
  user: User | null;
  activeFundId: string | null;
  workspaces: UserWorkspace[];
  isLoading: boolean;
  isAuthenticated: boolean;
  setAuth: (user: User, workspaces?: UserWorkspace[]) => void;
  setUser: (user: User | null) => void;
  setWorkspaces: (workspaces: UserWorkspace[]) => void;
  setActiveWorkspace: (fundId: string) => void;
  clearAuth: () => void;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

function getActiveWorkspaces(workspaces: UserWorkspace[]): UserWorkspace[] {
  return workspaces.filter((workspace) => workspace.isActive);
}

function applySelectedWorkspace(
  user: User,
  workspaces: UserWorkspace[],
  preferredFundId?: string | null,
): Pick<AuthState, 'user' | 'activeFundId'> {
  const activeWorkspaces = getActiveWorkspaces(workspaces);
  const selectedWorkspace =
    activeWorkspaces.find((workspace) => workspace.fundId === preferredFundId) ??
    activeWorkspaces.find((workspace) => workspace.fundId === user.fundId) ??
    activeWorkspaces[0] ??
    null;

  if (!selectedWorkspace) {
    return {
      user: {
        ...user,
        fundId: user.fundId ?? null,
      },
      activeFundId: user.fundId ?? null,
    };
  }

  return {
    user: {
      ...user,
      fundId: selectedWorkspace.fundId,
      role: selectedWorkspace.role,
    },
    activeFundId: selectedWorkspace.fundId,
  };
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      activeFundId: null,
      workspaces: [],
      isLoading: true,
      isAuthenticated: false,

      setAuth: (user, workspaces) => {
        set((state) => {
          const keepExistingWorkspaces = workspaces === undefined && state.user?.id === user.id;
          const nextWorkspaces = keepExistingWorkspaces ? state.workspaces : (workspaces ?? []);
          const preferredFundId =
            state.user?.id === user.id ? state.activeFundId ?? user.fundId : user.fundId;
          const resolved = applySelectedWorkspace(user, nextWorkspaces, preferredFundId);

          return {
            ...resolved,
            workspaces: nextWorkspaces,
            isAuthenticated: true,
            isLoading: false,
          };
        });
      },

      setUser: (user) => {
        set((state) => {
          if (!user) {
            return {
              user: null,
              activeFundId: null,
              workspaces: [],
              isAuthenticated: false,
              isLoading: false,
            };
          }

          const nextWorkspaces = state.user?.id === user.id ? state.workspaces : [];
          const resolved = applySelectedWorkspace(user, nextWorkspaces, state.activeFundId);

          return {
            ...resolved,
            workspaces: nextWorkspaces,
            isAuthenticated: true,
            isLoading: false,
          };
        });
      },

      setWorkspaces: (workspaces) => {
        set((state) => {
          if (!state.user) return { workspaces };

          const resolved = applySelectedWorkspace(
            state.user,
            workspaces,
            state.activeFundId ?? state.user.fundId,
          );

          return {
            ...resolved,
            workspaces,
          };
        });
      },

      setActiveWorkspace: (fundId) => {
        set((state) => {
          if (!state.user) return state;

          const selectedWorkspace = state.workspaces.find(
            (workspace) => workspace.isActive && workspace.fundId === fundId,
          );
          if (!selectedWorkspace) return state;

          return {
            activeFundId: selectedWorkspace.fundId,
            user: {
              ...state.user,
              fundId: selectedWorkspace.fundId,
              role: selectedWorkspace.role,
            },
          };
        });
      },

      clearAuth: () => {
        clearStoredAuthSession();
        if (get().user) queryClient.clear();
        set({
          user: null,
          activeFundId: null,
          workspaces: [],
          isAuthenticated: false,
          isLoading: false,
        });
      },

      logout: async () => {
        get().clearAuth();
        await authApi.logout().catch(() => {});
      },

      checkAuth: async () => {
        set({ isLoading: true });
        const observeRejection = <T,>(request: Promise<T>): Promise<T> => request.catch((error) => {
          // End a rejected session immediately even if the sibling request stalls.
          if (error instanceof ApiRequestError && error.status === 401) get().clearAuth();
          throw error;
        });
        try {
          const [user, workspaces] = await Promise.allSettled([
            observeRejection(authApi.getCurrentUser()),
            observeRejection(authApi.getWorkspaces()),
          ]);
          const rejectedSession = [user, workspaces].some((result) => result.status === 'rejected'
            && result.reason instanceof ApiRequestError && result.reason.status === 401);
          if (rejectedSession) get().clearAuth();
          else if (user.status === 'fulfilled' && workspaces.status === 'fulfilled') {
            get().setAuth(user.value, workspaces.value);
          }
          // Offline tabs and temporary service failures retain their saved session.
        } finally {
          set({ isLoading: false });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        activeFundId: state.activeFundId,
        workspaces: state.workspaces,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.isAuthenticated = Boolean(state.user);
          state.isLoading = true;
        }
      },
    },
  ),
);

function mirrorActiveFundContext(state: Pick<AuthState, 'user' | 'activeFundId'>): void {
  publishActiveFundContext({
    hasSession: Boolean(state.user),
    fundId: state.activeFundId ?? state.user?.fundId ?? null,
  });
}

mirrorActiveFundContext(useAuthStore.getState());
useAuthStore.subscribe(mirrorActiveFundContext);

startCrossTabAuthSync({
  readCurrentUserId: () => useAuthStore.getState().user?.id ?? null,
  onSessionEnded: () => useAuthStore.getState().clearAuth(),
});
